import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProblematicExtension {
  name: string;
  detected: boolean;
  selector?: string;
  property?: string;
}

export function BrowserCompatibilityChecker() {
  const [problematicExtensions, setProblematicExtensions] = useState<ProblematicExtension[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkExtensions = () => {
      const detected: ProblematicExtension[] = [];

      // Check for Grammarly
      if (document.querySelector('[data-grammarly-shadow-root]') || 
          document.querySelector('grammarly-extension') ||
          (window as any).grammarly) {
        detected.push({ name: 'Grammarly', detected: true });
      }

      // Check for common ad blockers that might interfere
      if (typeof (window as any).adblockDetector !== 'undefined' ||
          document.querySelector('[id*="adblock"]')) {
        detected.push({ name: 'Ad Blocker', detected: true });
      }

      // Check for React DevTools interference
      if ((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__?.renderers?.size > 1) {
        detected.push({ name: 'Multiple React DevTools', detected: true });
      }

      // Check for modified DOM elements that might indicate extension interference
      const modifiedElements = document.querySelectorAll('[data-new-gr-c-s-check-loaded]');
      if (modifiedElements.length > 0) {
        detected.push({ name: 'DOM-modifying Extension', detected: true });
      }

      setProblematicExtensions(detected);
    };

    // Initial check
    checkExtensions();

    // Recheck after a delay to catch lazy-loaded extensions
    const timer = setTimeout(checkExtensions, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (dismissed || problematicExtensions.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-2xl w-full px-4">
      <Alert variant="destructive" className="bg-background/95 backdrop-blur border-destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="flex items-center justify-between">
          Browser Extension Warning
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </AlertTitle>
        <AlertDescription className="mt-2">
          <p className="mb-2">
            Detected extensions that may interfere with the platform:
          </p>
          <ul className="list-disc list-inside mb-3 space-y-1">
            {problematicExtensions.map((ext, index) => (
              <li key={index} className="text-sm">{ext.name}</li>
            ))}
          </ul>
          <p className="text-sm">
            If you experience rendering issues, try:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 mt-1">
            <li>Opening the site in incognito/private mode</li>
            <li>Temporarily disabling these extensions</li>
            <li>Using a different browser</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
